import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Layout, Input, Icon, Spin, message, Empty } from "antd";
import {
  maskCnpj,
  encryptObject,
  getCnpjUrl,
  normalizeCnpj,
} from "../../../helpers/functions";
import { getApuracaoResultado } from "../../Company/Result/actions";
import { getCompanyRequest, putCompanyRequest } from "../../actions";

const { Sider } = Layout;

function Sidebar({ openModal }) {
  const location = useLocation();
  const iptRef = useRef(null);
  const empresa = useSelector((state) => state.companies);

  const [empresaFiltrada, setEmpresaFiltrada] = useState([]);
  const [isLoadingNovoContrato, setLoadingNovoContrato] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [moduloIcms] = useState(!!sessionStorage.getItem("icmsModule"));

  const initialRender = useCallback(() => {
    const { pathname } = location;

    setCollapsed(!!pathname?.toLowerCase().includes("resultado".toLowerCase()));
  }, [location.pathname]);

  useEffect(() => {
    initialRender();
  }, [initialRender]);

  useEffect(() => {
    setEmpresaFiltrada(empresa.list);
  }, [empresa.list]);

  const incluirPontuacaoCnpj = (cnpj) => {
    return maskCnpj(cnpj);
  };

  const doFiltrarEmpresa = (value) => {
    let novoEmpresas = [];

    novoEmpresas = empresa.list.filter(
      (item) =>
        item.Cnpj.search(value) !== -1 ||
        incluirPontuacaoCnpj(item.Cnpj).search(value) !== -1 ||
        normalizeCnpj(item.Cnpj).search(normalizeCnpj(value)) !== -1 ||
        item.RazaoSocial?.toLowerCase().search(value?.toLowerCase()) !== -1
    );

    setEmpresaFiltrada([...novoEmpresas]);
  };

  const selecionarEmpresa = (item) => {
    sessionStorage.setItem("eRecuperadorCompany", JSON.stringify(item));
    sessionStorage.setItem("eRecuperadorActiveTabCompany", 2);
    iptRef.current.state.value = "";
    doFiltrarEmpresa("");
  };

  const handleOpenGestaoContratosNovo = (e) => {
    e.preventDefault();

    if (!isLoadingNovoContrato) {
      if (!!getCnpjUrl()) {
        setLoadingNovoContrato(true);

        getCompanyRequest()
          .then(async (response) => {
            if (response.status === 200 && response.data?.StatusCode === 200) {
              await putCompanyRequest(response.data.Retorno);
            }

            getApuracaoResultado()
              .then(async (response) => {
                let credito = 0;
                if (
                  response.status === 200 &&
                  response.data?.StatusCode === 200
                ) {
                  credito = response.data.Retorno.CreditoApurado;
                  if (moduloIcms) {
                    credito += response.data.Retorno.CreditoApuradoIcms;
                  }
                }

                const data = {
                  modulo: moduloIcms
                    ? "eRecuperadorSegregadorIcms"
                    : "eRecuperadorPisCofins",
                  cnpj: getCnpjUrl(),
                  credito: credito.toFixed(2),
                };
                
                const objParam = encryptObject(data);

                window.open(
                  `${process.env.REACT_APP_GESTAO_CONTRATOS_URL
                  }/novo/tipo-template?d=${objParam}`,
                  "_blank"
                );
              })
              .catch((reason) => {
                message.error("Não foi possível obter os dados da empresa");
              })
              .finally(() => {
                setLoadingNovoContrato(false);
              });
          })
          .catch(() => {
            message.error("Não foi possível obter os dados da empresa");
            setLoadingNovoContrato(false);
          });
      }
    }
  };

  const handleOpenGestaoContratosListar = (e) => {
    e.preventDefault();
    const filtro = {
      modulo: moduloIcms
        ? "eRecuperadorSegregadorIcms"
        : "eRecuperadorPisCofins",
    };
    window.open(
      `${process.env.REACT_APP_GESTAO_CONTRATOS_URL}/home?f=${encryptObject(
        filtro
      )}`,
      "_blank"
    );
  };

  return (
    !!empresa.list?.length && (
      <Sider
        className="sidebar no-print"
        width={400}
        trigger={null}
        collapsible
        collapsed={collapsed}
      >
        <section className={`sidebar__body ${collapsed ? "d-none" : ""}`}>
          <h3 className="sidebar__body___title">filtrar empresas</h3>

          <Input
            ref={iptRef}
            className="form-control"
            placeholder="Buscar por CNPJ ou Razão Social"
            onChange={(e) => doFiltrarEmpresa(e.target.value)}
          />

          <div className="sidebar__body___companies">
            <div className="companies__header">
              <h3 className="companies__header___title">empresas</h3>
            </div>

            <div className="companies__list">
              {empresaFiltrada
                .sort((a, b) => a.RazaoSocial.localeCompare(b.RazaoSocial))
                ?.map((item) => {
                  return (
                    <div
                      className="companies__list___item"
                      key={item.Id + item.Cnpj}
                    >
                      <NavLink
                        className="item__action"
                        activeClassName="active"
                        to={`/empresa/${item.Cnpj}`}
                        onClick={() => selecionarEmpresa(item)}
                      >
                        <div className="item__action___header">
                          <h5 className="header__name">{item.RazaoSocial}</h5>
                          <span className="header__cnpj">
                            {maskCnpj(item.Cnpj)}
                          </span>
                        </div>
                      </NavLink>
                    </div>
                  );
                })}

              {!empresaFiltrada.length && (
                <Empty
                  description="Oops... Nenhuma empresa encontrada"
                  style={{ height: "calc(100vh - 280px)" }}
                />
              )}
            </div>
          </div>
        </section>

        <section className="sidebar__actions">
          <Icon
            className="trigger"
            type={collapsed ? "bars" : "close"}
            onClick={() => setCollapsed(!collapsed)}
          />
          <Link className="sidebar__actions___link" to="/recuperacao">
            Todas as {moduloIcms ? "segregações" : "recuperações"}
          </Link>
          <Link
            className="sidebar__actions___link"
            to="/"
            onClick={(e) => {
              openModal();
              e.preventDefault();
            }}
          >
            Novo processo de {moduloIcms ? "segregação" : "recuperação"}
          </Link>

          <Link
            className="sidebar__actions___link"
            to="/"
            onClick={handleOpenGestaoContratosListar}
          >
            Listar Propostas e Contratos
          </Link>

          {getCnpjUrl() && (
            <Link
              className="sidebar__actions___link"
              to="/"
              onClick={handleOpenGestaoContratosNovo}
            >
              <Spin spinning={isLoadingNovoContrato}>
                Nova Proposta ou Contrato
              </Spin>
            </Link>
          )}
        </section>
      </Sider>
    )
  );
}

export default Sidebar;
